import { sdk } from '../sdk';

export interface Office {
  _id: string;
  officeNo: number;
  name: string;
  controllerId: string | {
    _id: string;
    name: string;
    cnic: string;
    phone: string;
  };
  regionId?: string | {
    _id: string;
    name: string;
    regionId: number;
  };
  isCentral: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OfficesResponse {
  success: boolean;
  data: Office[];
  message: string;
}

export interface OfficeResponse {
  success: boolean;
  data: Office;
  message: string;
}

export interface CreateOfficeData {
  officeNo: number;
  name: string;
  controllerId: string;
  regionId?: string;
}

export interface UpdateOfficeData extends Partial<CreateOfficeData> {}

export class OfficesAPI {
  static async getAllOffices(params?: { page?: number; limit?: number; search?: string }): Promise<{ data: Office[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      
      const url = `/api/v1/offices${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await sdk.client.get(url);
      
      // If response is empty or undefined, return empty array
      if (!response) {
        return { data: [] };
      }
      
      // Check if the response has the expected structure
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch offices');
      }
      
      // Return data with pagination info if available
      return {
        data: response.data || [],
        pagination: response.pagination
      };
    } catch (error) {
      throw error;
    }
  }

  static async getOfficeById(id: string): Promise<Office> {
    try {
      const response = await sdk.client.get(`/api/v1/offices/${id}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch office');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async createOffice(data: CreateOfficeData): Promise<Office> {
    try {
      const response = await sdk.client.post('/api/v1/offices', data);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create office');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async updateOffice(id: string, data: UpdateOfficeData): Promise<Office> {
    try {
      const response = await sdk.client.put(`/api/v1/offices/${id}`, data);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update office');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async deleteOffice(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/offices/${id}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete office');
      }
      // Success - no need to return anything for delete operations
    } catch (error) {
      throw error;
    }
  }

  static async getOfficeStats(): Promise<Office[]> {
    try {
      const response = await sdk.client.get('/api/v1/offices/stats');
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch office statistics');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async searchOffices(query: string): Promise<Office[]> {
    try {
      const response = await sdk.client.get(`/api/v1/offices/search?q=${encodeURIComponent(query)}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to search offices');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async getNextOfficeNumber(regionId: string): Promise<number> {
    try {
      // Get the region to find the next available office number
      const response = await sdk.client.get(`/api/v1/regions/${regionId}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error('Failed to fetch region for office number calculation');
      }
      
      const region = response.data;
      
      // Get existing offices in this region
      const officesResponse = await sdk.client.get(`/api/v1/offices?regionId=${regionId}`);
      
      if (!officesResponse) {
        throw new Error('No response received');
      }
      
      if (!officesResponse.success) {
        throw new Error('Failed to fetch offices for number calculation');
      }
      
      const existingOffices = officesResponse.data;
      const existingNumbers = existingOffices.map((office: Office) => office.officeNo);
      
      // Find the next available number in the region's range
      for (let i = region.officeRange.start; i <= region.officeRange.end; i++) {
        if (!existingNumbers.includes(i)) {
          return i;
        }
      }
      
      throw new Error('No available office numbers in this region');
    } catch (error) {
      throw error;
    }
  }
}
